import { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, {
  Navigation,
  Pagination,
  A11y,
  Keyboard,
  Thumbs,
} from 'swiper'

import {
  StoryblokImage,
  getStoryblokImageServiceUrl,
} from '@cinch-nx/data-storyblok'
import { SvgIcon } from '@cinch-labs/design-system'

import styles from './car-review-gallery.module.css'
import { SlideChangeDetails } from '../../types/car-review-page'
import { useViewport } from '@cinch-labs/shared-util'

// eslint-disable-next-line react-hooks/rules-of-hooks
SwiperCore.use([Navigation, Pagination, A11y, Keyboard, Thumbs])

export interface Props {
  images: StoryblokImage[]
  updateGalleryPaginationNumber: (details: SlideChangeDetails) => void
  imageIndex: number | null
}

export const CarReviewGallery: React.FC<Props> = ({
  images = [],
  updateGalleryPaginationNumber,
  imageIndex,
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore>()
  const mainSwiper = useRef<SwiperCore | null>(null)

  const { vw } = useViewport({
    updateOnResize: true,
    updateOnOrientationChange: true,
  })

  useEffect(() => {
    console.log({ imageIndex })
  }, [imageIndex])

  const isDesktop = vw >= 1024

  const slides = generateSlides(images, [])
  const thumbs = generateThumbs(images, [])

  return (
    <>
      <button
        className={cx(styles.btn, styles.nextBtn)}
        onClick={() => {
          if (mainSwiper.current) {
            mainSwiper.current.slideNext()
          }
        }}
        aria-label="Next slide"
      >
        <SvgIcon name="chevron-right" size={24} />
      </button>
      <button
        className={cx(styles.btn, styles.prevBtn)}
        onClick={() => {
          if (mainSwiper.current) {
            mainSwiper.current.slidePrev()
          }
        }}
        aria-label="Previous slide"
      >
        <SvgIcon name="chevron-left" size={24} />
      </button>
      <Swiper
        keyboard
        loop
        slidesPerView={1}
        initialSlide={imageIndex || 0}
        className={styles.featured}
        thumbs={{
          swiper: thumbsSwiper,
          slideThumbActiveClass: styles.activeThumbnail,
        }}
        onSwiper={(swiper) => (mainSwiper.current = swiper)}
        onSlideChange={(swiper) => {
          const slideChangeDetails: SlideChangeDetails = {
            direction: swiper.touches.diff < 0 ? 'right' : 'left',
            activeIndex: swiper.realIndex + 1,
          }

          updateGalleryPaginationNumber(slideChangeDetails)
        }}
      >
        {slides}
      </Swiper>

      <Swiper
        onSwiper={(swiper) => {
          setThumbsSwiper(swiper)
        }}
        className={styles.thumbnails}
        slidesPerView={isDesktop ? 4.5 : 3.5}
        spaceBetween={10}
        watchSlidesVisibility
        watchSlidesProgress
      >
        {thumbs}
      </Swiper>
    </>
  )
}

export function generateSlides(images: StoryblokImage[], classNames: string[]) {
  return images.map((image, idx) => (
    <SwiperSlide
      className={cx(styles.slide, ...classNames)}
      key={`${image.id}-${idx}`}
    >
      <figure className={image.title ? styles.hasCaption : undefined}>
        {/* alt text is read out by screen readers - this field is required in Storyblok for image assets.
            if one isn't available no alt text is actually better than having placeholder text */}
        <img
          src={getStoryblokImageServiceUrl(image.filename, {
            width: 800,
            filters: [{ quality: 80 }, { fill: 'transparent' }],
            fitIn: { width: 800, height: 600 },
          })}
          alt={image?.alt ?? 'No Alt-Text provided.'}
        />

        {/* Switched the below from alt text to title text as alt text is not meant to be used as additional content,
            but should descriptive of what is in the image - by definition if you can see the image, then
            you don't need to also read the alt text as it should convey exactly the same thing.
            In this case, the title field can be used to add flavour or descriptive text instead, which is additional content */}
        {image.title && (
          <figcaption className={styles.slideFigcaption}>
            {image.title}
          </figcaption>
        )}
      </figure>
    </SwiperSlide>
  ))
}

export function generateThumbs(images: StoryblokImage[], classNames: string[]) {
  return images.map((image, idx) => (
    <SwiperSlide
      className={cx(styles.thumbnailSlide, ...classNames)}
      key={`${image.id}-${idx}-thumb`}
    >
      <img
        src={getStoryblokImageServiceUrl(image.filename, {
          filters: [{ quality: 40 }, { fill: 'transparent' }],
          fitIn: { width: 220, height: 165 },
        })}
        alt={`Thumbnail for slide #${idx + 1}`}
      />
    </SwiperSlide>
  ))
}
